/**
 * Contas do TikTok, YouTube e LinkedIn para o agendador.
 *
 * Conectar é ida e volta pelo navegador (`/api/integrations/social/<rede>/connect`).
 * Na volta, a URL traz `?social=<código>` — e, quando deu certo,
 * `&pendente=<id>`: o diálogo de escolha mostra as contas daquele login (o
 * LinkedIn devolve o perfil e as Páginas) e só as marcadas entram.
 *
 * Instagram e Facebook continuam em Configurações → Integrações: lá a conexão
 * serve também ao Inbox e às automações, não só às publicações.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { PermissionGuard } from "@/components/PermissionGuard";
import DashboardLayout from "@/components/DashboardLayout";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, ArrowLeft, Loader2, Plus, Unplug } from "lucide-react";
import { toast } from "sonner";
import { NETWORK_LABELS, type RedeExterna } from "@shared/social-publishing";
import { IconeRede } from "@/features/social/redes";

const RECADOS: Record<string, string> = {
  nao_configurado: "A integração com esta rede ainda não foi configurada neste servidor. Fale com o suporte do LeadConversa.",
  sem_permissao: "Seu perfil de acesso não pode conectar contas. Peça a um administrador.",
  state_invalido: "A conexão demorou demais ou o link não é válido. Tente conectar de novo.",
  cancelado: "A conexão foi cancelada antes de terminar.",
  sem_codigo: "A rede não devolveu a autorização. Tente de novo.",
  sessao_divergente: "Você voltou com outro usuário logado. Entre com o mesmo usuário e conecte de novo.",
  escopos_incompletos: "A permissão de publicar não foi concedida. Conecte de novo e aceite todas as permissões pedidas.",
  sem_contas:
    "Esse login não tem nenhuma conta que possa publicar. No YouTube, a conta Google precisa ter um canal; no LinkedIn, para Páginas, você precisa ser administrador da Página.",
  falha_troca: "A rede recusou a conexão agora. Tente de novo em alguns minutos.",
};

const AJUDA: Record<RedeExterna, string> = {
  tiktok: "Vídeos e fotos no perfil. Os números do perfil e dos vídeos aparecem em Insights.",
  youtube: "Vídeos e Shorts no canal, com título, visibilidade e números do YouTube Analytics.",
  linkedin: "Posts no seu perfil e nas Páginas da empresa que você administra. Números só das Páginas.",
};

const TIPO: Record<string, string> = { perfil: "Perfil", canal: "Canal", pessoa: "Perfil pessoal", organizacao: "Página" };

function ContasContent() {
  const busca = useSearch();
  const [, navegar] = useLocation();
  const params = useMemo(() => new URLSearchParams(busca), [busca]);
  const utils = trpc.useUtils();
  const lista = trpc.social.contas.lista.useQuery();
  const [pendenteId, setPendenteId] = useState<number | null>(null);
  const [desconectar, setDesconectar] = useState<{ id: number; nome: string } | null>(null);

  // Recado da volta do OAuth: mostra uma vez e limpa a URL.
  useEffect(() => {
    const codigo = params.get("social");
    if (!codigo) return;
    const p = Number(params.get("pendente"));
    if (codigo === "escolher" && p) setPendenteId(p);
    else if (RECADOS[codigo]) toast.error(RECADOS[codigo]);
    navegar("/social/contas", { replace: true });
  }, [params, navegar]);

  const desligar = trpc.social.contas.desconectar.useMutation({
    onSuccess: () => {
      toast.success("Conta desconectada. Posts agendados para ela vão falhar até reconectar.");
      void utils.social.contas.lista.invalidate();
      void utils.social.status.invalidate();
      setDesconectar(null);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const plano = lista.data?.plano;
  const noLimite = plano?.limite != null && plano.usadas >= plano.limite;

  return (
    <div className="space-y-5">
      <div>
        <Link href="/social" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Publicações
        </Link>
        <h1 className="mt-1 text-2xl font-semibold">Contas das redes sociais</h1>
        <p className="text-sm text-muted-foreground">
          Conecte o TikTok, o YouTube e o LinkedIn para agendar posts e ver os números. Instagram e Facebook ficam em{" "}
          <Link href="/settings?integration=instagram" className="text-primary hover:underline">
            Configurações → Integrações
          </Link>
          .
        </p>
        {plano && (
          <p className="mt-2 text-sm">
            Contas conectadas no plano: <strong>{plano.usadas}</strong>
            {plano.limite != null ? ` de ${plano.limite}` : " (sem limite)"}
            <span className="text-muted-foreground"> — cada Página do Facebook com o Instagram dela conta como uma.</span>
          </p>
        )}
      </div>

      {lista.isLoading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
      {lista.isError && <p className="text-sm text-red-600">{toUserFriendlyError(lista.error)}</p>}

      <div className="grid gap-4 lg:grid-cols-3">
        {lista.data?.redes.map((r) => (
          <Card key={r.rede}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <IconeRede rede={r.rede} className="h-5 w-5" /> {NETWORK_LABELS[r.rede]}
              </CardTitle>
              <CardDescription>{AJUDA[r.rede]}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {r.contas.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma conta conectada.</p>}
              {r.contas.map((c) => {
                const vence = c.venceEm ? new Date(c.venceEm) : null;
                const dias = vence ? Math.ceil((vence.getTime() - Date.now()) / 864e5) : null;
                return (
                  <div key={c.id} className="rounded border p-2 text-sm">
                    <div className="flex items-center gap-2">
                      {c.foto ? (
                        <img src={c.foto} alt="" className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <IconeRede rede={r.rede} className="h-5 w-5" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{c.nome}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {TIPO[c.kind] ?? c.kind}
                          {c.username ? ` · ${c.username.startsWith("@") ? c.username : `@${c.username}`}` : ""}
                        </p>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Desconectar"
                        title="Desconectar"
                        onClick={() => setDesconectar({ id: c.id, nome: c.nome })}
                      >
                        <Unplug className="h-4 w-4" />
                      </Button>
                    </div>
                    {c.erro && (
                      <p className="mt-2 flex items-start gap-1 text-xs text-red-700 dark:text-red-300">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {c.erro}
                      </p>
                    )}
                    {!c.erro && dias != null && dias <= 14 && (
                      <p className="mt-2 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {dias <= 0
                          ? "A conexão venceu. Reconecte para voltar a publicar."
                          : `A conexão vence em ${dias} ${dias === 1 ? "dia" : "dias"} — reconecte antes para não parar de publicar.`}
                      </p>
                    )}
                  </div>
                );
              })}
              {r.configurada ? (
                // No limite do plano ainda dá para RECONECTAR uma conta já
                // conectada (não ocupa vaga nova) — o servidor confere.
                noLimite && r.contas.length === 0 ? (
                  <Button variant="outline" className="w-full" disabled>
                    Limite de contas do plano atingido
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full" asChild>
                    <a href={`/api/integrations/social/${r.rede}/connect`}>
                      <Plus className="mr-2 h-4 w-4" />
                      {r.contas.length === 0 ? "Conectar" : noLimite ? "Reconectar" : "Conectar outra ou reconectar"}
                    </a>
                  </Button>
                )
              ) : (
                <p className="text-xs text-muted-foreground">Ainda não disponível neste servidor.</p>
              )}
              {r.rede === "linkedin" && r.configurada && !lista.data?.linkedinPaginas && (
                <p className="text-xs text-muted-foreground">
                  Por enquanto, só perfil pessoal: Páginas da empresa dependem da aprovação do LinkedIn.
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {pendenteId != null && <EscolherContas id={pendenteId} onFechar={() => setPendenteId(null)} />}

      <AlertDialog open={desconectar != null} onOpenChange={(v) => !v && setDesconectar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desconectar {desconectar?.nome}?</AlertDialogTitle>
            <AlertDialogDescription>
              O LeadConversa para de publicar e de ler os números desta conta. O histórico de posts e de insights continua
              aqui, e reconectar a mesma conta retoma de onde parou.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={() => desconectar && desligar.mutate({ id: desconectar.id })}>Desconectar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EscolherContas({ id, onFechar }: { id: number; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const pendente = trpc.social.contas.pendente.useQuery({ id }, { retry: false });
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());

  // Uma conta só, ou as já conectadas (reconexão): já vêm marcadas.
  useEffect(() => {
    const contas = pendente.data?.contas ?? [];
    if (contas.length === 0) return;
    const ja = contas.filter((c) => c.jaConectada).map((c) => c.externalId);
    setMarcadas(new Set(contas.length === 1 ? [contas[0].externalId] : ja));
  }, [pendente.data]);

  const confirmar = trpc.social.contas.confirmar.useMutation({
    onSuccess: (r) => {
      toast.success(r.conectadas === 1 ? "Conta conectada." : `${r.conectadas} contas conectadas.`);
      void utils.social.contas.lista.invalidate();
      void utils.social.status.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const descartar = trpc.social.contas.descartarPendente.useMutation({ onSettled: onFechar });

  const rede = pendente.data?.rede;
  return (
    <Dialog open onOpenChange={(v) => !v && descartar.mutate({ id })}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {rede && <IconeRede rede={rede} className="h-5 w-5" />} Escolha as contas
          </DialogTitle>
          <DialogDescription>Só as contas marcadas passam a publicar pelo LeadConversa.</DialogDescription>
        </DialogHeader>
        {pendente.isLoading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        {pendente.data === null && (
          <p className="text-sm text-muted-foreground">Esta conexão expirou (vale 30 minutos). Clique em conectar de novo.</p>
        )}
        <div className="space-y-2">
          {pendente.data?.contas.map((c) => (
            <label key={c.externalId} className="flex cursor-pointer items-center gap-3 rounded border p-2 text-sm">
              <Checkbox
                checked={marcadas.has(c.externalId)}
                onCheckedChange={(v) =>
                  setMarcadas((atual) => {
                    const novo = new Set(atual);
                    if (v === true) novo.add(c.externalId);
                    else novo.delete(c.externalId);
                    return novo;
                  })
                }
              />
              {c.foto && <img src={c.foto} alt="" className="h-7 w-7 rounded-full object-cover" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{c.nome}</span>
                <span className="text-xs text-muted-foreground">{TIPO[c.kind] ?? c.kind}</span>
              </span>
              {c.jaConectada && <Badge variant="outline">Já conectada</Badge>}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => descartar.mutate({ id })} disabled={descartar.isPending}>
            Cancelar
          </Button>
          <Button
            disabled={marcadas.size === 0 || confirmar.isPending || !pendente.data}
            onClick={() => confirmar.mutate({ id, externalIds: [...marcadas] })}
          >
            {confirmar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Conectar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SocialContas() {
  return (
    <PermissionGuard permission="social:manage_accounts">
      <DashboardLayout>
        <ContasContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
